import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Home } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBreadcrumbStore } from '@/stores/breadcrumb'

interface BreadcrumbItem {
  label: string
  path?: string
}

// Map route segments to translation keys
function useBreadcrumbs(): BreadcrumbItem[] {
  const { t } = useTranslation(['common', 'children', 'bookings', 'medicalHistory', 'payments', 'profile'])
  const location = useLocation()
  const pathname = location.pathname
  const breadcrumbExtra = useBreadcrumbStore((s) => s.extra)

  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0 || (segments.length === 1 && segments[0] === 'dashboard')) {
    return []
  }

  const items: BreadcrumbItem[] = []

  // First segment
  const first = segments[0]
  switch (first) {
    case 'children':
      items.push({ label: t('navigation.children'), path: '/children' })
      break
    case 'bookings':
      items.push({ label: t('navigation.bookings'), path: '/bookings' })
      break
    case 'medical-history':
      items.push({ label: t('navigation.medicalHistory'), path: '/medical-history' })
      break
    case 'payments':
      items.push({ label: t('navigation.payments'), path: '/payments' })
      break
    case 'profile':
      items.push({ label: t('navigation.profile') })
      break
    case 'dashboard':
      return []
    default:
      items.push({ label: first })
  }

  // Second segment
  if (segments.length >= 2) {
    const second = segments[1]
    switch (second) {
      case 'add':
        items.push({ label: t('actions.add', { defaultValue: 'Add' }) })
        break
      case 'edit':
        items.push({ label: t('actions.edit') })
        break
      case 'checkout':
        items.push({ label: t('navigation.makePayment') })
        break
      case 'history':
        items.push({ label: t('history', { ns: 'payments', defaultValue: 'History' }) })
        break
      case 'redirect':
        items.push({ label: t('redirect', { defaultValue: 'Redirect' }) })
        break
      default:
        // Numeric ID = editing an item
        if (/^\d+$/.test(second)) {
          items.push({ label: t('actions.edit') })
          if (breadcrumbExtra) {
            items.push({ label: breadcrumbExtra })
          }
        } else {
          items.push({ label: second })
        }
    }
  }

  return items
}

export function Breadcrumbs() {
  const items = useBreadcrumbs()

  if (items.length === 0) return null

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <li>
          <Link
            to="/dashboard"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <Home className="h-3.5 w-3.5" />
          </Link>
        </li>
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          return (
            <React.Fragment key={index}>
              <li>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
              </li>
              <li>
                {isLast || !item.path ? (
                  <span className={cn(isLast && "text-foreground font-medium")}>
                    {item.label}
                  </span>
                ) : (
                  <Link
                    to={item.path}
                    className="hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            </React.Fragment>
          )
        })}
      </ol>
    </nav>
  )
}
